import React, { useState, useEffect } from 'react';
import axios from 'axios';
import useAutoRefresh from '@/hooks/useAutoRefresh';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { 
  UserCircle, Users, Plus, Search, Phone, Mail, Edit, Trash2,
  RefreshCw, Shield, GraduationCap, Calendar, Star, Briefcase,
  Settings, Tag, X, CreditCard, Printer, Building2, MapPin, Camera, Upload
} from 'lucide-react';
import StaffIdCard from '@/components/center/StaffIdCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default roles — human-readable names ARE the role value (no slug conversion).
// This ensures "Receptionist" stays "Receptionist" throughout DB → API → UI → ID Card.
const DEFAULT_ROLES = [
  { id: 'Center Head', name: 'Center Head', color: 'purple' },
  { id: 'Trainer', name: 'Trainer', color: 'blue' },
  { id: 'Counselor', name: 'Counselor', color: 'emerald' },
  { id: 'Receptionist', name: 'Receptionist', color: 'amber' },
  { id: 'Sweeper', name: 'Sweeper', color: 'slate' },
];

const CenterStaffPage = () => {
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  
  // Role Management States
  const [roles, setRoles] = useState(DEFAULT_ROLES);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [editingRole, setEditingRole] = useState(null);
  
  const [staffForm, setStaffForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'Trainer',
    department: '',
    subjects: '',
    salary: '',
    photo: ''
  });

  // ID Card states
  const [idCardOpen, setIdCardOpen] = useState(false);
  const [idCardStaff, setIdCardStaff] = useState(null);
  const [centerInfo, setCenterInfo] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('center_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchStaff();
    fetchRoles();
    fetchCenterInfo();
  }, []);

  // Auto-refresh on tab focus — keep staff list & center info live.
  // Disabled while any modal/dialog is open so the focus event after a native
  // file picker (photo upload) does not unmount the dialog by flipping `loading`.
  useAutoRefresh(() => {
    fetchStaff();
    fetchCenterInfo();
  }, { enabled: !addDialogOpen && !editDialogOpen && !idCardOpen && !roleDialogOpen });

  const fetchCenterInfo = async () => {
    try {
      const res = await axios.get(`${API}/center/me`, { headers: getAuthHeaders() });
      setCenterInfo(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchStaff = async () => {
    // Only show the full-page spinner on the very first load; on subsequent
    // refreshes keep the current render intact so any open dialog (e.g. Edit
    // Staff with a photo-upload picker) doesn't unmount mid-action.
    const isFirstLoad = staff.length === 0;
    if (isFirstLoad) setLoading(true);
    try {
      const response = await axios.get(`${API}/center/staff`, { headers: getAuthHeaders() });
      setStaff(response.data || []);
    } catch (error) {
      console.error('Failed to fetch staff:', error);
      toast.error('Staff data load nahi ho saka');
      setStaff([]);
    } finally {
      if (isFirstLoad) setLoading(false);
    }
  };

  // Fetch custom roles from backend or localStorage
  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/center/roles`, { headers: getAuthHeaders() });
      if (response.data && response.data.length > 0) {
        setRoles(response.data);
      }
    } catch (error) {
      // If API doesn't exist yet, try localStorage
      const savedRoles = localStorage.getItem('center_staff_roles');
      if (savedRoles) {
        setRoles(JSON.parse(savedRoles));
      }
    }
  };

  // Add new role
  const handleAddRole = () => {
    if (!newRoleName.trim()) {
      toast.error('Role name enter karein');
      return;
    }

    // Role ID = the human-readable name (no slug). This guarantees DB ↔ UI consistency.
    const name = newRoleName.trim();
    if (roles.find(r => r.id.toLowerCase() === name.toLowerCase())) {
      toast.error('Yeh role already exist karta hai');
      return;
    }

    const colors = ['blue', 'purple', 'emerald', 'amber', 'rose', 'cyan', 'indigo', 'orange'];
    const newRole = {
      id: name,
      name,
      color: colors[roles.length % colors.length]
    };

    const updatedRoles = [...roles, newRole];
    setRoles(updatedRoles);
    localStorage.setItem('center_staff_roles', JSON.stringify(updatedRoles));
    saveRolesToBackend(updatedRoles);

    setNewRoleName('');
    toast.success(`Role "${name}" add ho gaya! ✅`);
  };

  // Edit role
  const handleEditRole = (role) => {
    setEditingRole({ ...role, newName: role.name });
  };

  const handleSaveEditRole = () => {
    if (!editingRole.newName.trim()) {
      toast.error('Role name enter karein');
      return;
    }
    
    const updatedRoles = roles.map(r => 
      r.id === editingRole.id 
        ? { ...r, name: editingRole.newName.trim() }
        : r
    );
    setRoles(updatedRoles);
    localStorage.setItem('center_staff_roles', JSON.stringify(updatedRoles));
    saveRolesToBackend(updatedRoles);
    
    setEditingRole(null);
    toast.success('Role update ho gaya! ✅');
  };

  // Delete role
  const handleDeleteRole = (roleId) => {
    // Check if any staff member has this role
    const staffWithRole = staff.filter(s => s.role === roleId);
    if (staffWithRole.length > 0) {
      toast.error(`${staffWithRole.length} staff members is role mein hain. Pehle unka role change karein.`);
      return;
    }
    
    // Don't allow deleting default roles
    if (['instructor', 'admin', 'counselor', 'lab_assistant'].includes(roleId)) {
      toast.error('Default roles delete nahi kar sakte');
      return;
    }
    
    const updatedRoles = roles.filter(r => r.id !== roleId);
    setRoles(updatedRoles);
    localStorage.setItem('center_staff_roles', JSON.stringify(updatedRoles));
    saveRolesToBackend(updatedRoles);
    
    toast.success('Role delete ho gaya!');
  };

  // Save roles to backend
  const saveRolesToBackend = async (rolesToSave) => {
    try {
      await axios.post(`${API}/center/roles`, { roles: rolesToSave }, { headers: getAuthHeaders() });
    } catch (error) {
      // Silently fail - localStorage is backup
      console.log('Roles saved to localStorage only');
    }
  };

  const handleAddStaff = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/center/staff`, {
        name: staffForm.name,
        email: staffForm.email || null,
        phone: staffForm.phone,
        role: staffForm.role,
        department: staffForm.department || null,
        subjects: staffForm.subjects || null,
        salary: staffForm.salary ? parseFloat(staffForm.salary) : null
      }, { headers: getAuthHeaders() });
      
      toast.success('Staff member add ho gaya');
      setAddDialogOpen(false);
      setStaffForm({ name: '', email: '', phone: '', role: 'Trainer', department: '', subjects: '', salary: '' });
      fetchStaff();
    } catch (error) {
      console.error('Failed to add staff:', error);
      toast.error(error.response?.data?.detail || 'Staff add nahi ho saka');
    }
  };

  const handleDeleteStaff = async (staffId) => {
    if (!window.confirm('Kya aap sure hain ki is staff ko delete karna hai?')) return;
    try {
      await axios.delete(`${API}/center/staff/${staffId}`, { headers: getAuthHeaders() });
      toast.success('Staff delete ho gaya');
      fetchStaff();
    } catch (error) {
      toast.error('Staff delete nahi ho saka');
    }
  };

  // Edit Staff Functions
  const handleOpenEditDialog = (member) => {
    setEditingStaff({
      id: member.id,
      name: member.name || '',
      email: member.email || '',
      phone: member.phone || '',
      role: member.role || 'Trainer',
      department: member.department || '',
      subjects: member.subjects || '',
      salary: member.salary ? String(member.salary) : '',
      photo: member.photo || ''
    });
    setEditDialogOpen(true);
  };

  const handleUpdateStaff = async (e) => {
    e.preventDefault();
    if (!editingStaff) return;
    
    try {
      await axios.put(`${API}/center/staff/${editingStaff.id}`, {
        name: editingStaff.name,
        email: editingStaff.email || null,
        phone: editingStaff.phone,
        role: editingStaff.role,
        department: editingStaff.department || null,
        subjects: editingStaff.subjects || null,
        salary: editingStaff.salary ? parseFloat(editingStaff.salary) : null,
        photo: editingStaff.photo || null
      }, { headers: getAuthHeaders() });
      
      toast.success('Staff information update ho gayi! ✅');
      setEditDialogOpen(false);
      setEditingStaff(null);
      fetchStaff();
    } catch (error) {
      console.error('Failed to update staff:', error);
      toast.error(error.response?.data?.detail || 'Staff update nahi ho saka');
    }
  };

  // Inline quick-update for a single field (used by inline role dropdown on cards).
  const quickUpdateField = async (member, field, value) => {
    try {
      await axios.put(`${API}/center/staff/${member.id}`, {
        name: member.name,
        email: member.email || null,
        phone: member.phone,
        role: member.role,
        department: member.department || null,
        subjects: member.subjects || null,
        salary: member.salary ?? null,
        photo: member.photo || null,
        [field]: value,
      }, { headers: getAuthHeaders() });
      // Optimistic local update
      setStaff(prev => prev.map(s => s.id === member.id ? { ...s, [field]: value } : s));
      toast.success(`${field === 'role' ? 'Role' : field} update ho gaya ✅`);
    } catch (error) {
      console.error('Quick update failed:', error);
      toast.error(error.response?.data?.detail || 'Update nahi ho saka');
    }
  };

  const getRoleColor = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    if (role) {
      const colorMap = {
        'blue': 'bg-blue-100 text-blue-700',
        'purple': 'bg-purple-100 text-purple-700',
        'emerald': 'bg-emerald-100 text-emerald-700',
        'amber': 'bg-amber-100 text-amber-700',
        'rose': 'bg-rose-100 text-rose-700',
        'cyan': 'bg-cyan-100 text-cyan-700',
        'indigo': 'bg-indigo-100 text-indigo-700',
        'orange': 'bg-orange-100 text-orange-700',
      };
      return colorMap[role.color] || 'bg-slate-100 text-slate-700';
    }
    return 'bg-slate-100 text-slate-700';
  };

  // Display-only formatting: slug → Title Case (NEVER cross-map to a different
  // role name). Old data may have slugs like "lab_assistant" or "instructor";
  // we render them as "Lab Assistant" / "Instructor" so the UI is readable,
  // but the underlying stored value is preserved as-is.
  const formatRoleSlug = (raw) => {
    const s = String(raw || '').trim();
    if (!s) return 'Staff';
    // If it looks like a friendly name already, keep it.
    if (/[A-Z]/.test(s) && !s.includes('_')) return s;
    return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const getRoleLabel = (roleId) => {
    if (!roleId) return 'Staff';
    const role = roles.find(r => r.id === roleId || r.name === roleId);
    if (role) return role.name;
    return formatRoleSlug(roleId);
  };

  const filteredStaff = staff.filter(s => 
    s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.subjects?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="center-staff-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Staff Management</h1>
          <p className="text-slate-500 mt-1">{staff.length} team members</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={() => setRoleDialogOpen(true)} variant="outline" className="gap-2">
            <Settings className="w-4 h-4" />
            Manage Roles
          </Button>
          <Button onClick={fetchStaff} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <Button onClick={() => setAddDialogOpen(true)} className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 gap-2">
            <Plus className="w-4 h-4" />
            Add Staff
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white shadow-lg">
          <div className="flex items-center gap-3">
            <GraduationCap className="w-8 h-8 text-blue-200" />
            <div>
              <p className="text-blue-100 text-sm">Trainers</p>
              <p className="text-2xl font-bold">{staff.filter(s => ['Trainer','instructor','trainer'].includes(s.role)).length}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white shadow-lg">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-purple-200" />
            <div>
              <p className="text-purple-100 text-sm">Admin Staff</p>
              <p className="text-2xl font-bold">{staff.filter(s => ['Admin','admin','Center Head','center_head'].includes(s.role)).length}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-4 text-white shadow-lg">
          <div className="flex items-center gap-3">
            <Users className="w-8 h-8 text-emerald-200" />
            <div>
              <p className="text-emerald-100 text-sm">Counselors</p>
              <p className="text-2xl font-bold">{staff.filter(s => ['Counselor','counselor','counsellor'].includes(s.role)).length}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-4 text-white shadow-lg">
          <div className="flex items-center gap-3">
            <Star className="w-8 h-8 text-amber-200" />
            <div>
              <p className="text-amber-100 text-sm">Avg Rating</p>
              <p className="text-2xl font-bold">4.8</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search staff..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Staff Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredStaff.map((member) => (
          <motion.div
            key={member.id}
            whileHover={{ y: -5 }}
            className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl transition-all"
          >
            <div className="p-5">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-xl">{member.name?.charAt(0)}</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-slate-900">{member.name}</h3>
                      <select
                        value={member.role || ''}
                        onChange={(e) => quickUpdateField(member, 'role', e.target.value)}
                        className={`mt-1 px-2 py-0.5 rounded-full text-xs font-medium border-0 outline-none cursor-pointer focus:ring-2 focus:ring-purple-300 ${getRoleColor(member.role)}`}
                        data-testid={`inline-role-select-${member.id}`}
                        title="Click to change role"
                      >
                        {/* Render the staff's current value first so legacy/custom values stay visible. */}
                        {member.role && !roles.find(r => r.id === member.role || r.name === member.role) && (
                          <option value={member.role}>{getRoleLabel(member.role)}</option>
                        )}
                        {roles.map(r => (
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </select>
                    </div>
                    {member.rating && (
                      <div className="flex items-center gap-1 text-amber-500">
                        <Star className="w-4 h-4 fill-current" />
                        <span className="text-sm font-medium">{member.rating}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="mt-4 space-y-2 text-sm text-slate-500">
                {member.department && (
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    <span>{member.department}</span>
                  </div>
                )}
                {member.subjects && (
                  <div className="flex items-center gap-2">
                    <GraduationCap className="w-4 h-4" />
                    <span>{member.subjects}</span>
                  </div>
                )}
                {member.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    <span>{member.email}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>{member.phone}</span>
                </div>
                {member.join_date && (
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>Joined: {member.join_date}</span>
                  </div>
                )}
              </div>
              
              {member.salary && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <div className="flex items-center justify-between">
                    <div className="text-sm">
                      <span className="text-slate-500">Salary:</span>
                      <span className="font-semibold text-slate-900 ml-1">₹{member.salary?.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-100">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex-1"
                  onClick={() => { setIdCardStaff(member); setIdCardOpen(true); }}
                  data-testid={`print-id-${member.id}`}
                >
                  <CreditCard className="w-4 h-4 mr-1" />
                  ID Card
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex-1"
                  onClick={() => handleOpenEditDialog(member)}
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Edit
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => handleDeleteStaff(member.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Add Staff Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCircle className="w-5 h-5 text-purple-600" />
              Add Staff Member
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddStaff} className="space-y-4">
            {/* Photo Upload */}
            <div>
              <Label>Profile Photo</Label>
              <div className="flex items-center gap-3 mt-1">
                <div className="w-16 h-16 rounded-full border-2 border-slate-200 overflow-hidden bg-slate-50 flex items-center justify-center flex-shrink-0">
                  {staffForm.photo ? (
                    <img src={staffForm.photo} alt="preview" className="w-full h-full object-cover" />
                  ) : (
                    <Camera className="w-6 h-6 text-slate-300" />
                  )}
                </div>
                <label className="flex-1 border-2 border-dashed border-slate-300 hover:border-purple-400 rounded-lg p-3 text-center cursor-pointer text-xs text-slate-500 hover:bg-purple-50/30 transition-colors">
                  <Upload className="w-4 h-4 mx-auto mb-1 text-purple-500" />
                  Click to upload (JPG/PNG, max 100KB)
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/jpg"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 100 * 1024) { toast.error('Photo size must be under 100 KB'); return; }
                      const r = new FileReader();
                      r.onload = (ev) => setStaffForm(prev => ({ ...prev, photo: ev.target.result }));
                      r.readAsDataURL(file);
                    }}
                  />
                </label>
                {staffForm.photo && (
                  <button type="button" onClick={() => setStaffForm({...staffForm, photo: ''})} className="text-red-500 hover:text-red-700">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            <div>
              <Label>Full Name *</Label>
              <Input
                value={staffForm.name}
                onChange={(e) => setStaffForm({...staffForm, name: e.target.value})}
                placeholder="Enter full name"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={staffForm.email}
                  onChange={(e) => setStaffForm({...staffForm, email: e.target.value})}
                  placeholder="email@example.com"
                  required
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={staffForm.phone}
                  onChange={(e) => setStaffForm({...staffForm, phone: e.target.value})}
                  placeholder="9876543210"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Role / पद</Label>
                <select
                  value={staffForm.role}
                  onChange={(e) => setStaffForm({...staffForm, role: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  data-testid="add-staff-role-select"
                >
                  {staffForm.role && !roles.some(r => r.id === staffForm.role) && (
                    <option value={staffForm.role}>{staffForm.role}</option>
                  )}
                  {roles.map(role => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Department</Label>
                <Input
                  value={staffForm.department}
                  onChange={(e) => setStaffForm({...staffForm, department: e.target.value})}
                  placeholder="e.g., IT, Admin"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Subjects</Label>
                <Input
                  value={staffForm.subjects}
                  onChange={(e) => setStaffForm({...staffForm, subjects: e.target.value})}
                  placeholder="e.g., Python, Tally"
                />
              </div>
              <div>
                <Label>Salary (Monthly)</Label>
                <Input
                  type="number"
                  value={staffForm.salary}
                  onChange={(e) => setStaffForm({...staffForm, salary: e.target.value})}
                  placeholder="₹25000"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-gradient-to-r from-purple-600 to-blue-600">
                <Plus className="w-4 h-4 mr-2" />
                Add Staff
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Staff Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              Edit Staff Member / स्टाफ एडिट करें
            </DialogTitle>
          </DialogHeader>
          {editingStaff && (
            <form onSubmit={handleUpdateStaff} className="space-y-4">
              <div>
                <Label>Profile Photo</Label>
                <div className="flex items-center gap-3 mt-1">
                  <div className="w-16 h-16 rounded-full border-2 border-slate-200 overflow-hidden bg-slate-50 flex items-center justify-center flex-shrink-0">
                    {editingStaff.photo ? (
                      <img src={editingStaff.photo} alt="preview" className="w-full h-full object-cover" />
                    ) : (
                      <Camera className="w-6 h-6 text-slate-300" />
                    )}
                  </div>
                  <label className="flex-1 border-2 border-dashed border-slate-300 hover:border-blue-400 rounded-lg p-3 text-center cursor-pointer text-xs text-slate-500 hover:bg-blue-50/30 transition-colors">
                    <Upload className="w-4 h-4 mx-auto mb-1 text-blue-500" />
                    Click to upload (JPG/PNG, max 100KB)
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/jpg"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 100 * 1024) { toast.error('Photo size must be under 100 KB'); return; }
                        const r = new FileReader();
                        r.onload = (ev) => setEditingStaff(prev => ({ ...prev, photo: ev.target.result }));
                        r.readAsDataURL(file);
                      }}
                    />
                  </label>
                  {editingStaff.photo && (
                    <button type="button" onClick={() => setEditingStaff({...editingStaff, photo: ''})} className="text-red-500 hover:text-red-700">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              <div>
                <Label>Full Name *</Label>
                <Input
                  value={editingStaff.name}
                  onChange={(e) => setEditingStaff({...editingStaff, name: e.target.value})}
                  placeholder="Enter full name"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email / ईमेल</Label>
                  <Input
                    type="email"
                    value={editingStaff.email}
                    onChange={(e) => setEditingStaff({...editingStaff, email: e.target.value})}
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <Label>Phone / फोन</Label>
                  <Input
                    value={editingStaff.phone}
                    onChange={(e) => setEditingStaff({...editingStaff, phone: e.target.value})}
                    placeholder="9876543210"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Role / पद</Label>
                  <select
                    value={editingStaff.role}
                    onChange={(e) => setEditingStaff({...editingStaff, role: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    data-testid="edit-staff-role-select"
                  >
                    {/* If the saved role isn't in the current roles list (e.g., legacy data),
                        still show it as an option so the value is preserved & not silently flipped. */}
                    {editingStaff.role && !roles.some(r => r.id === editingStaff.role) && (
                      <option value={editingStaff.role}>{editingStaff.role}</option>
                    )}
                    {roles.map(role => (
                      <option key={role.id} value={role.id}>{role.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Department / विभाग</Label>
                  <Input
                    value={editingStaff.department}
                    onChange={(e) => setEditingStaff({...editingStaff, department: e.target.value})}
                    placeholder="e.g., IT, Admin"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Subjects / विषय</Label>
                  <Input
                    value={editingStaff.subjects}
                    onChange={(e) => setEditingStaff({...editingStaff, subjects: e.target.value})}
                    placeholder="e.g., Python, Tally"
                  />
                </div>
                <div>
                  <Label>Salary / वेतन (Monthly)</Label>
                  <Input
                    type="number"
                    value={editingStaff.salary}
                    onChange={(e) => setEditingStaff({...editingStaff, salary: e.target.value})}
                    placeholder="₹25000"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => {
                  setEditDialogOpen(false);
                  setEditingStaff(null);
                }}>Cancel</Button>
                <Button type="submit" className="bg-gradient-to-r from-blue-600 to-purple-600">
                  <Edit className="w-4 h-4 mr-2" />
                  Update Staff / अपडेट करें
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Manage Roles Dialog */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5 text-indigo-600" />
              Manage Roles / पद प्रबंधन
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Add New Role */}
            <div className="flex gap-2">
              <Input
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                placeholder="New role name / नया पद नाम"
                onKeyPress={(e) => e.key === 'Enter' && handleAddRole()}
              />
              <Button onClick={handleAddRole} className="bg-indigo-600 hover:bg-indigo-700">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Roles List */}
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {roles.map((role) => (
                <div 
                  key={role.id} 
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg group hover:bg-slate-100 transition-colors"
                >
                  {editingRole?.id === role.id ? (
                    <div className="flex items-center gap-2 flex-1">
                      <Input
                        value={editingRole.newName}
                        onChange={(e) => setEditingRole({...editingRole, newName: e.target.value})}
                        className="h-8"
                        autoFocus
                        onKeyPress={(e) => e.key === 'Enter' && handleSaveEditRole()}
                      />
                      <Button size="sm" onClick={handleSaveEditRole} className="h-8 bg-emerald-600">
                        Save
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingRole(null)} className="h-8">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-3">
                        <span className={`w-3 h-3 rounded-full bg-${role.color}-500`}></span>
                        <span className="font-medium text-slate-700">{role.name}</span>
                        <span className="text-xs text-slate-400">
                          ({staff.filter(s => s.role === role.id).length} staff)
                        </span>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="h-8 w-8 p-0 text-blue-600 hover:bg-blue-100"
                          onClick={() => handleEditRole(role)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {!['instructor', 'admin', 'counselor', 'lab_assistant'].includes(role.id) && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="h-8 w-8 p-0 text-red-600 hover:bg-red-100"
                            onClick={() => handleDeleteRole(role.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
            
            <div className="pt-4 border-t">
              <p className="text-xs text-slate-500">
                💡 Default roles (Instructor, Admin, Counselor, Lab Assistant) cannot be deleted. 
                Custom roles can be edited or deleted.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============ Staff ID Card Dialog (preview only) ============ */}
      <Dialog open={idCardOpen} onOpenChange={setIdCardOpen}>
        <DialogContent className="max-w-md p-0 overflow-hidden">
          <DialogHeader className="px-6 pt-5 pb-3 border-b">
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-600" /> Staff ID Card
            </DialogTitle>
          </DialogHeader>
          {idCardStaff && (
            <div className="p-6 bg-slate-100">
              {/* Preview uses the SAME StaffIdCard component as print, just scaled up for on-screen readability.
                  CR80 = 54mm × 85.6mm. At scale 1.6 the visible box is ~ 324 × 513 px. */}
              <div
                style={{
                  width: `calc(54mm * 1.6)`,
                  height: `calc(85.6mm * 1.6)`,
                  margin: '0 auto',
                }}
              >
                <StaffIdCard staff={idCardStaff} center={centerInfo} scale={1.6} />
              </div>

              <div className="flex gap-2 mt-4">
                <Button variant="outline" onClick={() => setIdCardOpen(false)} className="flex-1">Close</Button>
                <Button
                  onClick={() => {
                    // Open dedicated print page — preview == print == final output
                    window.open(`/print/staff-id/${idCardStaff.id}`, '_blank', 'width=520,height=720');
                  }}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  data-testid="print-staff-id"
                >
                  <Printer className="w-4 h-4 mr-2" /> Print
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CenterStaffPage;
